import React, { useState } from "react";
import EmployeeDetailsModal from "./EmployeeDetailsModal";
import moment from "moment";

export const PositionEmployeeTotalsTable = ({
  filteredRecords = [],
  dateRange = {},
  employeeApiService,
  employees = [],
  positionName = "Tip"
}) => {
  const isOther = String(positionName || "").trim().toLowerCase().includes("other");
  const [modalRecipient, setModalRecipient] = useState(null);
  const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);
  const [employeeDetailsError, setEmployeeDetailsError] = useState(null);
  const [showEmployeeDetailsModal, setShowEmployeeDetailsModal] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  if (!filteredRecords || filteredRecords.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No employee data available for the selected date range
      </div>
    );
  }

  // Aggregate data per employee
  const employeeTotals = filteredRecords.reduce((acc, record) => {
    const empName = record.employee || "Unknown";
    if (!acc[empName]) {
      acc[empName] = {
        employee: empName,
        tipsReceived: 0,
        tax: 0
      };
    }

    acc[empName].tipsReceived += parseFloat(record.tip_out_cash) || 0;
    acc[empName].tax += parseFloat(record.tax) || 0;
    return acc;
  }, {});

  const employeeTotalsArray = Object.values(employeeTotals).sort((a, b) =>
    a.employee.localeCompare(b.employee)
  );

  const totalTipsReceived = employeeTotalsArray.reduce(
    (sum, emp) => sum + emp.tipsReceived,
    0
  );
  const totalTax = employeeTotalsArray.reduce((sum, emp) => sum + emp.tax, 0);
  const totalPaidOut = totalTipsReceived - totalTax;

  const handleRecipientNameClick = async (recipientName, e) => {
    if (e) e.stopPropagation();

    const employee = employees.find((emp) => emp.name === recipientName);

    if (employee && employeeApiService) {
      setLoadingEmployeeDetails(true);
      setEmployeeDetailsError(null);
      setShowEmployeeDetailsModal(true);

      try {
        const startDate = dateRange?.start
          ? moment(dateRange.start).format("YYYY-MM-DD")
          : moment().subtract(30, "days").format("YYYY-MM-DD");
        const endDate = dateRange?.end
          ? moment(dateRange.end).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD");

        const [completeDataResponse, performanceResponse] = await Promise.all([
          employeeApiService.getCompleteEmployeeData(employee.id, {
            startDate,
            endDate
          }),
          employeeApiService.getEmployeePerformanceMetrics(employee.id, {
            startDate,
            endDate
          })
        ]);

        if (completeDataResponse.success && performanceResponse.success) {
          setEmployeeDetails({
            employee,
            completeData: completeDataResponse?.data?.body?.data,
            performanceMetrics: performanceResponse?.data?.body?.data
          });
        } else {
          setEmployeeDetailsError("Failed to load employee details");
        }
      } catch (error) {
        console.error("Error loading employee details:", error);
        setEmployeeDetailsError("Error loading employee details");
      } finally {
        setLoadingEmployeeDetails(false);
      }
    } else {
      setModalRecipient(recipientName);
      setShowEmployeeDetailsModal(true);
    }
  };

  const closeEmployeeDetailsModal = () => {
    setShowEmployeeDetailsModal(false);
    setEmployeeDetails(null);
    setEmployeeDetailsError(null);
  };

  return (
    <>
      <div className="space-y-6">
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Employee
                </th>
                {isOther && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    Position
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Total Tip Out
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Total Tax
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Total Paid Out
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {employeeTotalsArray.map((emp) => {
                const paidOut = emp.tipsReceived - emp.tax;
                const empObj = employees.find((e) => e.name === emp.employee);
                const empPos = empObj?.role || emp.position || "-";
                return (
                  <tr key={emp.employee} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <button
                        className="font-medium text-blue-700 hover:text-blue-900 hover:underline cursor-pointer"
                        onClick={(e) =>
                          handleRecipientNameClick(emp.employee, e)
                        }
                      >
                        {emp.employee || "-"}
                      </button>
                    </td>
                    {isOther && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-200">
                          {empPos}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${formatCurrency(emp.tipsReceived)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {emp.tax > 0
                        ? `- $${formatCurrency(emp.tax)}`
                        : "$0.00"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      ${formatCurrency(paidOut)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  Total
                </td>
                {isOther && <td className="px-6 py-4 border-b"></td>}
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  ${formatCurrency(totalTipsReceived)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  ${formatCurrency(totalTax)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                  ${formatCurrency(totalPaidOut)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <EmployeeDetailsModal
        isOpen={showEmployeeDetailsModal}
        onClose={closeEmployeeDetailsModal}
        loading={loadingEmployeeDetails}
        error={employeeDetailsError}
        employeeDetails={employeeDetails}
        dateRange={dateRange}
        fallbackName={modalRecipient}
      />
    </>
  );
};

export default PositionEmployeeTotalsTable;
